/**
 * FmDiameterHistogram — the only diameter-distribution renderer.
 *
 * Bars by diameter class, stacked by species code, with the planned cut drawn
 * as its own series on top in the tender-PDF cut red. The red stems are the
 * server's removed counts per class, summed over species; the species colours
 * are the served staying stems. Buyers and owners read the red portion as
 * exactly what leaves the forest, so nothing here derives a count from another
 * and the `--fm-chart-cut` tone must not change.
 *
 * `removedDistributions` absent → the served stems only, no cut is drawn.
 * `keptLabel` → the staying stems draw as one green series, the buyer PDF's
 * picture, so the in-app chart and the stand page show the same two series.
 *
 * Classes always draw in ascending diameter order, whatever the served order.
 */
import React from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import type { FmChartSize, FmHistogramBase, FmHistogramCut } from './chartTypes';
import {
  CHART_GRID_PROPS,
  chartMargin,
  CHART_XAXIS_PROPS,
  CHART_YAXIS_PROPS,
  xAxisTitle,
  yAxisTitle,
} from './FmChartChrome.constants';
import { FmChartTooltip } from './FmChartChrome';
import { FmChartFrame } from './FmChartFrame';
import { CUT_KEY, KEPT_KEY, MODEL_KEY, histogramRows, speciesKey } from './chartData';
import {
  CHART_CUT_COLOR,
  CHART_CUT_STROKE,
  CHART_KEPT_COLOR,
  paletteSeries,
  paletteSpecies,
} from './palette';

export type FmDiameterHistogramProps = FmHistogramBase &
  FmHistogramCut & {
    /** Accessible name when the histogram is used outside `FmChart`. */
    ariaLabel?: string;
    'data-testid'?: string;
  };

interface CutShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  index?: number;
}

/** The cut rect keeps a stable test id: buyer-facing contracts count these. */
const CutRect: React.FC<CutShapeProps> = ({ x = 0, y = 0, width = 0, height = 0, index }) =>
  height > 0 ? (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={CHART_CUT_COLOR}
      stroke={CHART_CUT_STROKE}
      strokeWidth={0.5}
      data-testid="histogram-cut-rect"
      data-bin={index}
    />
  ) : (
    <g />
  );

const STORY_HEIGHT = 280;
const COMPACT_HEIGHT = 220;

export const FmDiameterHistogram: React.FC<FmDiameterHistogramProps> = ({
  distributions,
  removedDistributions,
  modelLine,
  xLabel,
  yLabel,
  cutLabel,
  keptLabel,
  presentation = 'compact',
  ariaLabel,
  'data-testid': testId,
}) => {
  const rows = histogramRows(
    distributions,
    removedDistributions,
    modelLine,
    keptLabel === undefined ? 'species' : 'kept',
  );
  if (rows.length === 0) return null;
  const size: FmChartSize = presentation === 'story' ? 'lg' : 'md';
  const staying =
    keptLabel === undefined
      ? distributions.map((entry) => ({
          key: speciesKey(entry.speciesCode),
          label: entry.label,
          color: paletteSpecies(entry.speciesCode),
        }))
      : [{ key: KEPT_KEY, label: keptLabel, color: CHART_KEPT_COLOR }];
  const formatStems = (value: number): string => String(Math.round(value));
  return (
    <div role={ariaLabel ? 'group' : undefined} aria-label={ariaLabel} data-testid={testId}>
      <FmChartFrame
        size={size}
        height={presentation === 'story' ? STORY_HEIGHT : COMPACT_HEIGHT}
        data-kind="histogram"
      >
        <ComposedChart data={rows} margin={chartMargin(yLabel)} barCategoryGap="12%" accessibilityLayer>
          <CartesianGrid {...CHART_GRID_PROPS} />
          <XAxis dataKey="diameter" {...CHART_XAXIS_PROPS} label={xAxisTitle(xLabel)} />
          <YAxis {...CHART_YAXIS_PROPS} label={yAxisTitle(yLabel)} allowDecimals={false} />
          <Tooltip content={<FmChartTooltip formatValue={formatStems} />} />
          {staying.map((entry) => (
            <Bar
              key={entry.key}
              dataKey={entry.key}
              name={entry.label}
              stackId="stems"
              fill={entry.color}
              isAnimationActive={false}
            />
          ))}
          {removedDistributions !== undefined ? (
            <Bar
              dataKey={CUT_KEY}
              name={cutLabel}
              stackId="stems"
              fill={CHART_CUT_COLOR}
              shape={<CutRect />}
              isAnimationActive={false}
            />
          ) : null}
          {modelLine ? (
            <Line
              dataKey={MODEL_KEY}
              name={modelLine.label}
              type="linear"
              stroke={paletteSeries(0)}
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          ) : null}
        </ComposedChart>
      </FmChartFrame>
    </div>
  );
};
